import React, { useMemo, useState } from 'react';
import { cn } from 'src/lib/utils';
import {
  buildResumeUploadProgressMeta,
  getResumeProgressDisplayLabel,
} from 'src/utils/resumeUploadProgressMeta';
import './ResumeProcessingOverlay.css';

/** @deprecated Import from `src/utils/resumeUploadProgressMeta` */
export { buildResumeUploadProgressMeta, getResumeProgressDisplayLabel };

/** Show modal subtitle during processing only from this step index onward (0-based: 2 = 3rd step). */
const MIN_PROCESSING_STEP_INDEX_FOR_MODAL_SUB = 2;

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Combined resume popup: upload | processing | success | failed.
 * `progressMeta` should be built from reference-data `resume_upload_process_labels` (see dashboard).
 */
export function ResumeProcessingOverlay({
  open,
  /** 'upload' | 'processing' | 'success' | 'failed' */
  surface,
  processingStep = 0,
  processingPercent = 0,
  /** Optional label from API (e.g. step-2 interpolation). */
  apiLabel = null,
  failedAtStep = 0,
  onReviewProfile,
  onManualFill,
  onReupload,
  /** From `buildResumeUploadProgressMeta(referenceData.resumeUploadProcessLabels)` */
  progressMeta: progressMetaProp,
  /* Upload surface */
  fileInputRef,
  selectedFile = null,
  onFileInputChange,
  onClearFile,
  onUploadClick,
  onSkipManual,
  isUploading = false,
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [localUploadError, setLocalUploadError] = useState('');

  const progressMeta = useMemo(
    () => progressMetaProp ?? buildResumeUploadProgressMeta([]),
    [progressMetaProp]
  );

  const clampedStep = Math.min(
    Math.max(0, processingStep),
    progressMeta.maxListStepIndex
  );
  const pct = Math.min(100, Math.max(0, processingPercent));

  const dynamicLabel = useMemo(
    () =>
      getResumeProgressDisplayLabel({
        status: surface === 'failed' ? 'failed' : 'processing',
        step: clampedStep,
        label: apiLabel,
        labelForStepId: progressMeta.labelForStepId,
        fallbackLabel: progressMeta.fallbackChainLabel,
      }),
    [
      surface,
      clampedStep,
      apiLabel,
      progressMeta.labelForStepId,
      progressMeta.fallbackChainLabel,
    ]
  );

  if (!open) return null;

  const showUpload = surface === 'upload';
  const showProcessing = surface === 'processing';
  const showSuccess = surface === 'success';
  const showFailed = surface === 'failed';

  const iconVariant = showUpload
    ? 'rpc-modal-icon--upload'
    : showProcessing
      ? 'rpc-modal-icon--processing'
      : showSuccess
        ? 'rpc-modal-icon--done'
        : 'rpc-modal-icon--failed';

  const heading = showUpload
    ? 'Upload your resume'
    : showProcessing
      ? 'Analyzing your resume'
      : showSuccess
        ? 'All set!'
        : "We couldn't process your resume";

  const sub = showUpload
    ? "We'll fill in your profile automatically"
    : showSuccess
      ? 'Your details have been filled in successfully.'
      : showFailed
        ? 'You can fill in your details or try uploading again.'
        : dynamicLabel;

  const showModalSub =
    !showProcessing || clampedStep >= MIN_PROCESSING_STEP_INDEX_FOR_MODAL_SUB;

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f || !onFileInputChange) return;
    const dt = new DataTransfer();
    dt.items.add(f);
    const synthetic = { target: { files: dt.files } };
    onFileInputChange(synthetic);
  };

  const validateAndPassFile = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setLocalUploadError('Please upload a PDF, DOC, or DOCX file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setLocalUploadError('File is too large. Maximum size is 5 MB');
      return;
    }
    setLocalUploadError('');
    onFileInputChange?.({ target: { files: fileList } });
  };

  const uploadErrorVisible = Boolean(localUploadError);

  return (
    <div
      className="rpc rpc-overlay pointer-events-auto"
      data-resume-processing-overlay=""
    >
      <div className="rpc-modal">
        <div className={cn('rpc-modal-icon', iconVariant)}>
          {showProcessing && <div className="rpc-spin-ring" aria-hidden />}
          <svg
            className="rpc-ic-upload"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <svg
            className="rpc-ic-doc"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <svg
            className="rpc-ic-check"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            stroke="currentColor"
            aria-hidden
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            className="rpc-ic-warn"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <h2 className="rpc-modal-heading !text-center">{heading}</h2>
        {showModalSub ? (
          <p className="rpc-modal-sub !text-center">{sub}</p>
        ) : null}

        {/* UPLOAD */}
        <div className={cn('rpc-state', showUpload && 'is-active')}>
          {!selectedFile ? (
            <div
              role="button"
              tabIndex={0}
              className={cn('rpc-dropzone', isDragOver && 'rpc-dragover')}
              onClick={() => fileInputRef?.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef?.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="rpc-dropzone-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <div className="rpc-dropzone-text">
                <span>Browse</span> or drag & drop
              </div>
              <div className="rpc-dropzone-hint">
                PDF, DOC, or DOCX · Max 5 MB
              </div>
            </div>
          ) : (
            <div className={cn('rpc-dropzone-file', 'is-visible')}>
              <div className="rpc-dropzone-file-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="rpc-dropzone-file-info">
                <div className="rpc-dropzone-file-name">
                  {selectedFile.name}
                </div>
                <div className="rpc-dropzone-file-size">
                  {formatBytes(selectedFile.size)}
                </div>
              </div>
              <button
                type="button"
                className="rpc-dropzone-file-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocalUploadError('');
                  onClearFile?.();
                }}
                aria-label="Remove file"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          <div
            className={cn(
              'rpc-upload-error',
              uploadErrorVisible && 'is-visible'
            )}
          >
            {localUploadError}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={(e) => {
              validateAndPassFile(e.target.files);
              e.target.value = '';
            }}
          />

          <button
            type="button"
            className="rpc-btn rpc-btn--primary"
            disabled={!selectedFile || isUploading}
            onClick={onUploadClick}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {isUploading ? 'Uploading…' : 'Upload & process'}
          </button>
          <button
            type="button"
            className="rpc-btn rpc-btn--ghost"
            onClick={() => onSkipManual?.()}
          >
            I&apos;ll fill in manually
          </button>
        </div>

        {/* PROCESSING / SUCCESS / FAILED — shared step list */}
        <div
          className={cn(
            'rpc-state',
            (showProcessing || showSuccess || showFailed) && 'is-active'
          )}
        >
          <div className="rpc-steps">
            {Array.from({ length: progressMeta.uiStepCount }, (_, i) => {
              let stepClass = 'rpc-step';
              if (showSuccess) {
                stepClass = cn(stepClass, 'is-done');
              } else if (showFailed) {
                if (i < failedAtStep) stepClass = cn(stepClass, 'is-done');
                else if (i === failedAtStep)
                  stepClass = cn(stepClass, 'is-failed-at');
                else stepClass = cn(stepClass, 'is-skipped');
              } else if (i < clampedStep) stepClass = cn(stepClass, 'is-done');
              else if (i === clampedStep)
                stepClass = cn(stepClass, 'is-active');

              return (
                <div key={i} className={stepClass}>
                  <div className="rpc-step-dot">
                    <div className="rpc-step-ic rpc-step-ic-check">
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <polyline points="2 6 5 9 10 3" />
                      </svg>
                    </div>
                    <div className="rpc-step-ic rpc-step-ic-warn">
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <line x1="6" y1="3.5" x2="6" y2="7" />
                        <line x1="6" y1="9" x2="6.01" y2="9" />
                      </svg>
                    </div>
                  </div>
                  <span>{progressMeta.listLabels[i] ?? ''}</span>
                </div>
              );
            })}
          </div>

          <div
            className={cn('rpc-progress-section', showFailed && 'is-hidden')}
          >
            <div className="rpc-progress-track">
              <div
                className={cn('rpc-progress-fill', showSuccess && 'is-success')}
                style={{ width: `${showSuccess ? 100 : pct}%` }}
              />
            </div>
            <div className="rpc-progress-meta">
              <span
                className={cn(
                  'rpc-progress-label',
                  showSuccess && 'is-success'
                )}
              >
                {showSuccess ? 'Resume processed' : dynamicLabel}
              </span>
              <span
                className={cn('rpc-progress-pct', showSuccess && 'is-success')}
              >
                {showSuccess ? '100%' : `${Math.round(pct)}%`}
              </span>
            </div>
          </div>
        </div>

        <div className={cn('rpc-modal-cta', showSuccess && 'is-visible')}>
          <button
            type="button"
            className="rpc-btn rpc-btn--primary"
            onClick={onReviewProfile}
          >
            Review your profile
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div className={cn('rpc-modal-cta', showFailed && 'is-visible')}>
          <div className="rpc-cta-stack">
            <button
              type="button"
              className="rpc-btn rpc-btn--primary"
              onClick={onManualFill}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Fill in your details
            </button>
            <div className="rpc-cta-or">or</div>
            <button
              type="button"
              className="rpc-btn rpc-btn--outline"
              onClick={onReupload}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Re-upload resume
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
